import { memo } from 'react'

import { pctToView, type Ring } from '@/geo/types'

/**
 * A walk / fly / swim / climb route (§4.3.1 `Path`): a polyline with
 * chevrons showing the direction of travel, so "this path runs the wrong
 * way" is visible without clicking anything.
 */

export interface PathShapeProps {
  id: string
  kind: string
  points: Ring
  closed?: boolean
  colour: string
  selected?: boolean
  hovered?: boolean
  ghost?: boolean
  label?: string
  onPointerDown?: (ev: React.PointerEvent<SVGElement>) => void
}

/** One chevron every this many percent of travelled length. */
const CHEVRON_SPACING = 8

export const PathShape = memo(function PathShape({ id, kind, points, closed, colour, selected, hovered, ghost, label, onPointerDown }: PathShapeProps) {
  if (points.length < 2) return null
  const view = points.map((p) => pctToView(p))
  const d = `M${view[0]![0]},${view[0]![1]}` + view.slice(1).map((p) => `L${p[0]},${p[1]}`).join('') + (closed ? 'Z' : '')
  const width = selected ? 0.55 : hovered ? 0.45 : 0.35

  return (
    <g role="img" aria-label={label ?? kind} opacity={ghost ? 0.7 : 1}>
      <title>{label ? `${label} (${kind})` : kind}</title>
      <path
        data-id={id}
        d={d}
        fill="none"
        stroke={colour}
        strokeWidth={width}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={ghost ? '1.2 0.8' : undefined}
        vectorEffect="non-scaling-stroke"
        style={{ pointerEvents: ghost ? 'none' : 'stroke', cursor: 'pointer' }}
        onPointerDown={onPointerDown}
      />
      {chevrons(view).map((c, i) => (
        <path
          key={i}
          d={`M${-0.8},${-0.6} L0,0 L${-0.8},${0.6}`}
          transform={`translate(${c.x} ${c.y}) rotate(${c.deg})`}
          fill="none"
          stroke={colour}
          strokeWidth={width * 0.9}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          pointerEvents="none"
        />
      ))}
    </g>
  )
})

function chevrons(view: readonly [number, number][]): { x: number; y: number; deg: number }[] {
  const out: { x: number; y: number; deg: number }[] = []
  let carry = CHEVRON_SPACING / 2
  for (let i = 0; i + 1 < view.length; i++) {
    const a = view[i]!
    const b = view[i + 1]!
    const len = Math.hypot(b[0] - a[0], b[1] - a[1])
    const deg = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI
    let travelled = carry
    while (travelled <= len) {
      const t = travelled / len
      out.push({ x: a[0] + (b[0] - a[0]) * t, y: a[1] + (b[1] - a[1]) * t, deg })
      travelled += CHEVRON_SPACING
    }
    carry = travelled - len
  }
  return out
}
